import type { KeyboardEvent, ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';
import { getColors, typography } from '../tokens';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';

const useStyles = makeStyles()(theme => {
  const colors = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';

  return {
    grid: {
      display: 'flex',
      flexDirection: 'row',
      gap: 16,
      padding: '24px 48px',
      [theme.breakpoints.down('lg')]: {
        flexDirection: 'column',
        padding: '24px 24px',
      },
      [theme.breakpoints.down('md')]: {
        gap: 12,
        padding: '12px 12px',
      },
      [theme.breakpoints.down('sm')]: {
        gap: 12,
        padding: '12px 12px',
      },
    },
    featuredCard: {
      background: colors.background.paper,
      borderRadius: '8px',
      padding: '24px',
      cursor: 'pointer',
      transition: 'border-color 0.15s ease, background 0.15s ease',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      textAlign: 'left',
      textTransform: 'none',
      position: 'relative',
      flex: '1 1 0',
      overflow: 'hidden',
      minHeight: 300,
      '&:hover': {
        borderColor: colors.primary.main,
        background: colors.background.elevated,
      },
      [theme.breakpoints.down('lg')]: {
        width: '100%',
        minHeight: 280,
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: 240,
      },
    },
    cardsRight: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '1fr 1fr',
      gap: 16,
      flex: '2 2 0',
      [theme.breakpoints.down('lg')]: {
        flex: '1 1 auto',
        width: '100%',
        gridTemplateColumns: '1fr 1fr',
      },
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gap: 12,
      },
      [theme.breakpoints.down('sm')]: {
        gap: 12,
      },
    },
    card: {
      background: colors.background.paper,
      borderRadius: '16px',
      padding: '12px 16px 44px 16px',
      cursor: 'pointer',
      transition: 'border-color 0.15s ease, background 0.15s ease',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      textAlign: 'left',
      textTransform: 'none',
      position: 'relative',
      gap: 12,
      minHeight: 180,
      maxHeight: 180,
      '&:hover': {
        borderColor: colors.primary.main,
        background: colors.background.elevated,
      },
      [theme.breakpoints.down('sm')]: {
        padding: '10px 12px 40px 12px',
      },
    },
    featuredHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      position: 'relative',
      zIndex: 1,
    },
    featuredTextBlock: {
      display: 'flex',
      flexDirection: 'column',
    },
    featuredSubtitle: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.9rem',
      fontWeight: 400,
      color: colors.text.secondary,
      margin: 0,
    },
    featuredTitle: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '1.4rem',
      fontWeight: 700,
      color: colors.text.primary,
      margin: '4px 0 0',
    },
    btnDisabled: {
      opacity: 0.4,
    },
    cardIcon: {
      flexShrink: 0,
      width: '12%',
      height: 'auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    cardBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      minWidth: 0,
    },
    cardTitle: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.95rem',
      fontWeight: 600,
      color: colors.text.primary,
      userSelect: 'none',
      textAlign: 'left',
    },
    cardChips: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    cardArrow: {
      position: 'absolute',
      bottom: 14,
      right: 16,
      fontSize: '1.2rem',
      lineHeight: 1,
      background: 'linear-gradient(135deg, #0078D6, #17E3CC)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    chip: {
      alignSelf: 'flex-start',
      background: 'transparent',
      borderRadius: 12,
      border: isLight
        ? '1.5px solid rgba(0, 0, 0, 0.25)'
        : '1.5px solid rgba(255, 255, 255, 0.5)',
      padding: '3px 10px',
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.75rem',
      color: colors.text.primary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
    },
  };
});

export interface LandingCardItem {
  icon: ReactNode;
  title: string;
  chips?: string[];
  loading?: boolean;
  onClick: () => void;
  disabled?: boolean;
}

export interface LandingFeaturedCard {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  chips?: string[];
  loading?: boolean;
  backgroundDecoration?: ReactNode;
  onClick: () => void;
}

interface LandingPageGridProps {
  featuredCard: LandingFeaturedCard;
  cards: LandingCardItem[];
  minHeight?: number | string;
}

function handleKey(e: KeyboardEvent, cb: () => void) {
  if (e.key === 'Enter' || e.key === ' ') cb();
}

export const LandingPageGrid = ({
  featuredCard,
  cards,
  minHeight,
}: Readonly<LandingPageGridProps>) => {
  const { classes, cx } = useStyles();

  return (
    <div className={classes.grid} style={minHeight ? { minHeight } : undefined}>
      <Button
        className={classes.featuredCard}
        tabIndex={0}
        onClick={featuredCard.onClick}
        onKeyDown={e => handleKey(e, featuredCard.onClick)}
      >
        {featuredCard.backgroundDecoration}
        <div className={classes.featuredHeader}>
          {featuredCard.icon}
          <div className={classes.featuredTextBlock}>
            {featuredCard.subtitle && (
              <p className={classes.featuredSubtitle}>
                {featuredCard.subtitle}
              </p>
            )}
            <p className={classes.featuredTitle}>{featuredCard.title}</p>
            {featuredCard.loading ? (
              <div className={classes.cardChips}>
                <Skeleton variant="rounded" width={80} height={20} />
                <Skeleton variant="rounded" width={100} height={20} />
                <Skeleton variant="rounded" width={90} height={20} />
              </div>
            ) : (
              featuredCard.chips &&
              featuredCard.chips.length > 0 && (
                <div className={classes.cardChips}>
                  {featuredCard.chips.map(chip => (
                    <span key={chip} className={classes.chip}>
                      {chip}
                    </span>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
        <span className={classes.cardArrow}>&rarr;</span>
      </Button>

      <div className={classes.cardsRight}>
        {cards.map(card => (
          <Button
            key={card.title}
            className={cx(classes.card, card.disabled && classes.btnDisabled)}
            tabIndex={0}
            onClick={card.onClick}
            disabled={card.disabled}
            onKeyDown={e => handleKey(e, card.onClick)}
          >
            <span className={classes.cardIcon}>{card.icon}</span>
            <div className={classes.cardBody}>
              <span className={classes.cardTitle}>{card.title}</span>
              {card.loading ? (
                <div className={classes.cardChips}>
                  <Skeleton variant="rounded" width={70} height={18} />
                  <Skeleton variant="rounded" width={90} height={18} />
                  <Skeleton variant="rounded" width={75} height={18} />
                </div>
              ) : (
                card.chips &&
                card.chips.length > 0 && (
                  <div className={classes.cardChips}>
                    {card.chips.map(chip => (
                      <span key={chip} className={classes.chip}>
                        {chip}
                      </span>
                    ))}
                  </div>
                )
              )}
            </div>
            <span className={classes.cardArrow}>&rarr;</span>
          </Button>
        ))}
      </div>
    </div>
  );
};
